import React, { useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import { RobotOutlined, CopyOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { askCurves } from '../utils/aiClient';
import { useI18n } from '../i18n/i18n';

/**
 * 多曲线叠加 SVG 组件（按绝对电流 Isec 坐标）
 * props:
 * - series: [{ label, color, points: [{ I, t }] }]
 * - width, height
 * - padding
 * - showLegend
 * - showTooltips (boolean)
 * - enableBoxZoom (boolean)
 * - colorBlindMode (boolean)
 * - refLineKeys (string[])
 * - onViewportChange({ zoomX, zoomY, panX, panY })
 * - zoomX, zoomY (>=1 为放大比例；默认 1)
 * - panX, panY (-1~1 相对范围；默认 0)
 */
const CurveOverlay = ({ series = [], width = 720, height = 360, padding = { left: 48, right: 24, top: 16, bottom: 28 }, showLegend = true, zoomX = 1, zoomY = 1, panX = 0, panY = 0, showTooltips = true, enableBoxZoom = true, colorBlindMode = false, refLineKeys = [], onViewportChange }) => {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiSource, setAiSource] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const allPoints = (series || []).flatMap(s => s.points || []);
  const axesType = useMemo(() => {
    const types = new Set((series || []).map(s => s?.axesType).filter(Boolean));
    if (types.has('Z')) return 'Z';
    if (types.has('Id-Ir')) return 'Id-Ir';
    return series?.[0]?.axesType || 'I-t';
  }, [series]);
  const palette = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#13c2c2', '#722ed1'];
  const cbPalette = ['#0072B2', '#D55E00', '#009E73', '#CC79A7', '#F0E442', '#56B4E9', '#E69F00', '#000000'];
  const getColor = (s, i) => s.color || (colorBlindMode ? cbPalette[i % cbPalette.length] : palette[i % palette.length]);
 
  const handleAskAI = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setAiError('');
    try {
      // 传入当前叠加的曲线，坐标系自动在客户端处理
      const res = await askCurves(series || [], axesType, { locale: 'zh' });
      setAiText(res.text || '');
      setAiSource(res.source || '');
      setAiPrompt(res.prompt || '');
    } catch (err) {
      setAiError(err?.message || '分析失败');
    } finally {
      setAiLoading(false);
    }
  };
  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(String(text || '')); } catch (_) {}
  };
 
  // 统一刻度与提示单位格式化：电流(A/kA)、时间(s/ms)、阻抗(Ω/kΩ)
  const formatAxisValue = (num, kind) => {
    if (!isFinite(num)) return '\u2014';
    switch (kind) {
      case 'current': {
        const v = Math.abs(num);
        const unit = v >= 1000 ? 'kA' : 'A';
        const val = v >= 1000 ? num / 1000 : num;
        const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
        return `${val.toFixed(digits)}${unit}`;
      }
      case 'time': {
        if (num >= 1) {
          const v = num;
          const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
          return `${v.toFixed(digits)}s`;
        } else {
          const vms = num * 1000;
          const digits = vms >= 100 ? 0 : vms >= 10 ? 1 : 2;
          return `${vms.toFixed(digits)}ms`;
        }
      }
      case 'impedance': {
        const v = Math.abs(num);
        const unit = v >= 1000 ? 'kΩ' : 'Ω';
        const val = v >= 1000 ? num / 1000 : num;
        const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
        return `${val.toFixed(digits)}${unit}`;
      }
      default:
        return `${num}`;
    }
  };
 
  const IminBase = !allPoints.length ? 0 : Math.min(...allPoints.map(p => p.I || 0));
  const ImaxBase = !allPoints.length ? 1 : Math.max(...allPoints.map(p => p.I || 0));
  const TmaxBase = !allPoints.length ? 1 : Math.max(...allPoints.map(p => (p.t === Infinity ? 0 : p.t) || 0));
    // 保护处理：若范围无效，给出默认
  const Ispan = (ImaxBase - IminBase) || 1;
  const Tspan = TmaxBase || 1;
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const zx = Math.max(1, zoomX || 1);
  const zy = Math.max(1, zoomY || 1);
  const cx = IminBase + Ispan * clamp(panX || 0, -0.9, 0.9) * 0.5 + Ispan / 2;
  const cy = Tspan * clamp(panY || 0, -0.9, 0.9) * 0.5 + Tspan / 2;
  const IspanVisible = Ispan / zx;
  const TspanVisible = Tspan / zy;
  let Imin = cx - IspanVisible / 2;
  let Imax = cx + IspanVisible / 2;
  let Tmin = 0; // Y 轴从 0 开始
  let Tmax = cy + TspanVisible / 2;
    // 边界约束
  Imin = clamp(Imin, IminBase, ImaxBase - 1e-9);
  Imax = clamp(Imax, IminBase + 1e-9, ImaxBase);
  Tmin = clamp(Tmin, 0, TmaxBase);
  Tmax = clamp(Tmax, 1e-9, TmaxBase);
  const xScale = (I) => padding.left + ((I - Imin) / (Imax - Imin)) * (width - padding.left - padding.right);
  const yScale = (T) => (height - padding.bottom) - (((T - Tmin) / (Tmax - Tmin)) * (height - padding.top - padding.bottom));
  const xUnscale = (x) => Imin + ((x - padding.left) / (width - padding.left - padding.right)) * (Imax - Imin);
  const yUnscale = (y) => Tmin + (((height - padding.bottom - y) / (height - padding.top - padding.bottom)) * (Tmax - Tmin));

  const xTicks = Array.from({ length: 6 }).map((_, i) => Imin + i * (Imax - Imin) / 5);
  const yTicks = Array.from({ length: 6 }).map((_, i) => Tmin + i * (Tmax - Tmin) / 5);

  if (!allPoints.length && axesType !== 'Z') {
    return <div style={{ color: '#999' }}>无曲线数据</div>;
  }

  const handleMouseMove = (e) => {
    if (!showTooltips) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; const y = e.clientY - rect.top;
    const Iq = xUnscale(x);
    const items = (series || []).map((s, i) => {
      const pts = (s.points || []).filter(p => isFinite(p.t));
      if (!pts.length) return null;
      // 找到最接近 Iq 的点
      let best = pts[0]; let dmin = Math.abs(best.I - Iq);
      for (let j = 1; j < pts.length; j++) {
        const d = Math.abs(pts[j].I - Iq);
        if (d < dmin) { dmin = d; best = pts[j]; }
      }
      const color = (s.color || (colorBlindMode ? cbPalette[i % cbPalette.length] : palette[i % palette.length]));
      return { label: s.label || `曲线${i + 1}`, color, I: best.I, t: best.t };
    }).filter(Boolean);
    setHoverInfo({ x, y, items });
  };

  const handleMouseLeave = () => setHoverInfo(null);

  const startDrag = (e) => {
    if (!enableBoxZoom || e.button !== 0) return;
    // const { xUnscale, yUnscale } = plane;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; const y = e.clientY - rect.top;
    setDragRect({ x1: x, y1: y, x2: x, y2: y });
  };
  const moveDrag = (e) => {
    if (!dragRect) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; const y = e.clientY - rect.top;
    setDragRect(prev => ({ ...prev, x2: x, y2: y }));
  };
  const endDrag = () => {
    if (!dragRect) return;
    const { x1, y1, x2, y2 } = dragRect;
    const xMin = Math.min(x1, x2); const xMax = Math.max(x1, x2);
    // 缩放/平移逻辑需要适配多坐标系，暂缓
    setDragRect(null);
    if (typeof onViewportChange === 'function') {
      // onViewportChange({ zoomX: zxNew, zoomY: zyNew, panX: panXNew, panY: panYNew });
    }
  };

  const handleKeyDown = (e) => {
    if (typeof onViewportChange !== 'function') return;
    switch (e.key) {
      case 'ArrowLeft': onViewportChange({ zoomX: zoomX, zoomY: zoomY, panX: clamp((panX || 0) - 0.05, -0.9, 0.9), panY }); break;
      case 'ArrowRight': onViewportChange({ zoomX: zoomX, zoomY: zoomY, panX: clamp((panX || 0) + 0.05, -0.9, 0.9), panY }); break;
      case 'ArrowUp': onViewportChange({ zoomX: zoomX, zoomY: zoomY, panX, panY: clamp((panY || 0) - 0.05, -0.9, 0.9) }); break;
      case 'ArrowDown': onViewportChange({ zoomX: zoomX, zoomY: zoomY, panX, panY: clamp((panY || 0) + 0.05, -0.9, 0.9) }); break;
      case '+':
      case '=':
        onViewportChange({ zoomX: clamp((zoomX || 1) + 0.5, 1, 8), zoomY: clamp((zoomY || 1) + 0.5, 1, 8), panX, panY });
        break;
      case '-':
      case '_':
        onViewportChange({ zoomX: clamp((zoomX || 1) - 0.5, 1, 8), zoomY: clamp((zoomY || 1) - 0.5, 1, 8), panX, panY });
        break;
      default: break;
    }
  };

  // =========================== 按坐标系渲染分支 ===========================
  // Z 平面 (距离保护)：在 R/X 坐标系按分区角度(θ)绘制扇形区域，缺省时回退圆形
  const renderZPlane = () => {
    const allZones = (series || []).flatMap(s => s.meta?.zones || []);
    if (!allZones.length) return <div style={{ color: '#999' }}>无距离保护区域数据</div>;
    const Zmax = Math.max(...allZones.map(z => z?.Z || 0)) * 1.2;
    if (!isFinite(Zmax) || Zmax <= 0) return <div style={{ color: '#999' }}>距离保护区域数据无效</div>;
    const Rmin = -Zmax, Rmax = Zmax;
    const Xmin = -Zmax, Xmax = Zmax;
    const xScaleZ = (R) => padding.left + ((R - Rmin) / (Rmax - Rmin)) * (width - padding.left - padding.right);
    const yScaleZ = (X) => (height - padding.bottom) - (((X - Xmin) / (Xmax - Xmin)) * (height - padding.top - padding.bottom));
    const xTicksZ = Array.from({ length: 5 }).map((_, i) => Rmin + i * (Rmax - Rmin) / 4);
    const yTicksZ = Array.from({ length: 5 }).map((_, i) => Xmin + i * (Xmax - Xmin) / 4);

    // 将极坐标 (Z, θ) 转为 R/X
    const polarToRX = (Z, deg) => {
      const rad = (deg * Math.PI) / 180;
      const R = Z * Math.cos(rad);
      const X = Z * Math.sin(rad);
      return { R, X };
    };

    // 生成扇形边界点集（用于 path 填充）
    const arcPoints = (Z, degStart, degEnd, steps = 36) => {
      const pts = [];
      const s = Math.min(degStart, degEnd);
      const e = Math.max(degStart, degEnd);
      const step = (e - s) / steps;
      for (let k = 0; k <= steps; k++) {
        const d = s + k * step;
        const { R, X } = polarToRX(Z, d);
        pts.push([xScaleZ(R), yScaleZ(X)]);
      }
      return pts;
    };

    // 反变换：屏幕坐标到 R/X
    const xUnscaleZ = (x) => Rmin + ((x - padding.left) / (width - padding.left - padding.right)) * (Rmax - Rmin);
    const yUnscaleZ = (y) => Xmin + (((height - padding.bottom - y) / (height - padding.top - padding.bottom)) * (Xmax - Xmin));
    const onMouseMoveZ = (e) => {
      if (!showTooltips) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left; const y = e.clientY - rect.top;
      const Rq = xUnscaleZ(x);
      const Xq = yUnscaleZ(y);
      setHoverInfo({ x, y, items: [{ label: '光标', color: '#666', R: Rq, X: Xq }] });
    };
    const onMouseLeaveZ = () => setHoverInfo(null);

    // Kb（若可用）用于徽标与提示
    const kbList = (series || []).map(s => s?.meta?.secondHarmonicBlocking).filter(v => typeof v === 'number' && isFinite(v));
    const kb = kbList.length > 0 ? kbList[0] : null;

    return (
      <>
      <svg className="z-plane-overlay-svg" width={width} height={height} style={{ background: '#fff', border: '1px solid #eee' }} onMouseMove={onMouseMoveZ} onMouseLeave={onMouseLeaveZ}>
        {/* 坐标轴主线 */}
        <line x1={padding.left} y1={yScaleZ(0)} x2={width - padding.right} y2={yScaleZ(0)} stroke="#999" strokeWidth={1} />
        <line x1={xScaleZ(0)} y1={padding.top} x2={xScaleZ(0)} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        {/* 轴箭头 */}
        <line x1={width - padding.right - 10} y1={yScaleZ(0) - 5} x2={width - padding.right} y2={yScaleZ(0)} stroke="#999" strokeWidth={1} />
        <line x1={width - padding.right - 10} y1={yScaleZ(0) + 5} x2={width - padding.right} y2={yScaleZ(0)} stroke="#999" strokeWidth={1} />
        <line x1={xScaleZ(0) - 5} y1={padding.top + 10} x2={xScaleZ(0)} y2={padding.top} stroke="#999" strokeWidth={1} />
        <line x1={xScaleZ(0) + 5} y1={padding.top + 10} x2={xScaleZ(0)} y2={padding.top} stroke="#999" strokeWidth={1} />
        {/* 网格与刻度标签 */}
        {xTicksZ.map((R, idx) => (
          <g key={`xt-z-${idx}`}>
            <line x1={xScaleZ(R)} y1={padding.top} x2={xScaleZ(R)} y2={height - padding.bottom} stroke="#f2f2f2" strokeWidth={1} />
            <text x={xScaleZ(R) - 12} y={yScaleZ(0) + 18} fontSize={12} fill="#666">{formatAxisValue(R, 'impedance')}</text>
          </g>
        ))}
        {yTicksZ.map((X, idx) => (
          <g key={`yt-z-${idx}`}>
            <line x1={padding.left} y1={yScaleZ(X)} x2={width - padding.right} y2={yScaleZ(X)} stroke="#f2f2f2" strokeWidth={1} />
            <text x={xScaleZ(0) + 8} y={yScaleZ(X) + 4} fontSize={12} fill="#666">{formatAxisValue(X, 'impedance')}</text>
          </g>
        ))}
        {(() => {
          const zUnitSuffix = Math.max(...[...xTicksZ, ...yTicksZ].map(v => Math.abs(v))) >= 1000 ? 'kΩ' : 'Ω';
          return (
            <>
              <text x={width - padding.right - 40} y={yScaleZ(0) + 18} fontSize={12} fill="#333">R({zUnitSuffix})</text>
              <text x={xScaleZ(0) + 8} y={padding.top + 12} fontSize={12} fill="#333">X({zUnitSuffix})</text>
            </>
          );
        })()}
        {/* 坐标系徽标（含 Kb 提示） */}
        <g aria-label="axes-badge-z">
          <rect x={padding.left + 8} y={padding.top + 8} width={kb != null ? 220 : 110} height={kb != null ? 40 : 24} rx={6} ry={6} fill="#fafafa" stroke="#e6e6e6" />
          <text x={padding.left + 16} y={padding.top + 24} fontSize={12} fill="#444">坐标系: R/X（Z）</text>
          {kb != null && (
            <text x={padding.left + 16} y={padding.top + 38} fontSize={12} fill="#666">Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</text>
          )}
        </g>
        {/* 原点标记 */}
        <g>
          <line x1={xScaleZ(0) - 4} y1={yScaleZ(0)} x2={xScaleZ(0) + 4} y2={yScaleZ(0)} stroke="#bbb" />
          <line x1={xScaleZ(0)} y1={yScaleZ(0) - 4} x2={xScaleZ(0)} y2={yScaleZ(0) + 4} stroke="#bbb" />
        </g>

        {(series || []).map((s, i) => {
          const color = getColor(s, i);
          const lineAngleDeg = s.meta?.lineAngleDeg ?? 75; // 缺省线路阻抗角
          return (
            <g key={`zones-${i}`}>
              {/* 线路阻抗角参考线 */}
              {isFinite(lineAngleDeg) && (
                (() => {
                  const { R: Rref, X: Xref } = polarToRX(Zmax * 0.9, lineAngleDeg);
                  return (
                    <g>
                      <line x1={xScaleZ(0)} y1={yScaleZ(0)} x2={xScaleZ(Rref)} y2={yScaleZ(Xref)} stroke="#bfbfbf" strokeDasharray="5,4" strokeWidth={1} />
                      <text x={xScaleZ(Rref)} y={yScaleZ(Xref) - 6} fontSize={12} fill="#888">θ≈{lineAngleDeg.toFixed(0)}°</text>
                    </g>
                  );
                })()
              )}
              {(s.meta?.zones || []).map((zone, j) => {
                const Z = +zone.Z || 0;
                if (!isFinite(Z) || Z <= 0) return null;
                const spanDeg = (zone.spanDeg ?? (15 + j * 5));
                const startDeg = (zone.thetaStartDeg ?? (lineAngleDeg - spanDeg));
                const endDeg = (zone.thetaEndDeg ?? (lineAngleDeg + spanDeg));
                const marginPct = +zone.marginPct || 0;
                const label = zone.label || `Zone ${j + 1}`;

                // 扇形区域路径
                const arc = arcPoints(Z, startDeg, endDeg, 42);
                const cx = xScaleZ(0);
                const cy = yScaleZ(0);
                const d = [
                  `M ${cx},${cy}`,
                  ...arc.map((p, idx) => `${idx === 0 ? 'L' : 'L'} ${p[0]},${p[1]}`),
                  `L ${cx},${cy}`
                ].join(' ');

                // 裕度圈（若有），以缩小半径绘制细边框
                const Zm = marginPct > 0 ? Z * (1 - marginPct / 100) : null;
                const arcM = Zm ? arcPoints(Zm, startDeg, endDeg, 28) : null;
                const dM = arcM ? [
                  `M ${cx},${cy}`,
                  ...arcM.map((p, idx) => `${idx === 0 ? 'L' : 'L'} ${p[0]},${p[1]}`),
                  `L ${cx},${cy}`
                ].join(' ') : null;

                // 边界线：扇形两侧半径线
                const { R: Rstart, X: Xstart } = polarToRX(Z, startDeg);
                const { R: Rend, X: Xend } = polarToRX(Z, endDeg);
                const xStart = xScaleZ(Rstart);
                const yStart = yScaleZ(Xstart);
                const xEnd = xScaleZ(Rend);
                const yEnd = yScaleZ(Xend);

                // 标签位置：取中角度、0.6×半径
                const midDeg = (startDeg + endDeg) / 2;
                const { R: Rlab, X: Xlab } = polarToRX(Z * 0.6, midDeg);
                const xLab = xScaleZ(Rlab);
                const yLab = yScaleZ(Xlab);

                return (
                  <g key={`zone-${i}-${j}`}>
                    <path d={d} fill={`${color}22`} stroke={color} strokeWidth={2} opacity={0.9} />
                    {/* 裕度圈 */}
                    {dM && <path d={dM} fill="none" stroke={color} strokeDasharray="4,3" strokeWidth={1} opacity={0.8} />}
                    {/* 边界线 */}
                    <line x1={cx} y1={cy} x2={xStart} y2={yStart} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.9} />
                    <line x1={cx} y1={cy} x2={xEnd} y2={yEnd} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.9} />
                    {/* 标签 */}
                    <text x={xLab + 6} y={yLab - 6} fontSize={12} fill={color}>
                      {label} · Z={formatAxisValue(Z, 'impedance')}{marginPct ? ` · 裕度${marginPct}%` : ''}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      {axesType === 'Z' && showTooltips && hoverInfo && (
        <div
          role="tooltip"
          aria-live="polite"
          style={{ position: 'absolute', left: hoverInfo.x + 12, top: hoverInfo.y + 12, background: '#ffffff', border: '1px solid #e6e6e6', borderRadius: 6, padding: '6px 8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontSize: 12 }}
        >
          {kb != null && (
            <div style={{ color: '#666', marginBottom: 4 }}>Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, background: '#999' }} />
            <span style={{ color: '#333' }}>光标</span>
            <span style={{ color: '#666' }}>R≈{formatAxisValue(hoverInfo.items?.[0]?.R, 'impedance')}</span>
            <span style={{ color: '#666' }}>X≈{formatAxisValue(hoverInfo.items?.[0]?.X, 'impedance')}</span>
          </div>
        </div>
      )}
      </>
    );
  };

  // Id-Ir 平面 (差动特性)：points 中 I=Ir, t=Id
  const renderIdIrPlane = () => {
    const ptsAll = (series || []).flatMap(s => s.points || []);
    if (!ptsAll.length) return <div style={{ color: '#999' }}>无差动特性数据</div>;
    const IrMin = 0;
    // 结合元特性扩展范围：考虑分段阈值与速断定值
    const charList = (series || []).map(s => s.meta?.characteristic || {});
    const maxThresholdFromSegments = Math.max(0, ...charList.map(c => Array.isArray(c.segments) && c.segments.length > 0 ? Math.max(...c.segments.map(seg => Number(seg.threshold) || 0)) : 0));
    const instMax = Math.max(0, ...charList.map(c => Number(c.instantaneousSecondary) || 0));
    const IrFromPoints = Math.max(...ptsAll.map(p => p.I || 0)) || 0;
    const IrMax = Math.max(1, IrFromPoints * 1.1, maxThresholdFromSegments * 1.1, instMax * 2);
    const IdMin = 0;
    const IdFromPoints = Math.max(...ptsAll.map(p => p.t || 0)) || 0;
    const maxSlopeEstimate = Math.max(0.5, ...charList.map(c => Number(c.slope) || 0));
    const basePickupEstimate = Math.max(0.4, ...charList.map(c => Number(c.diffPickup) || 0));
    const IdMax = Math.max(1, IdFromPoints * 1.2, instMax * 1.3, basePickupEstimate * 4, maxSlopeEstimate * IrMax + basePickupEstimate);
    const xScaleId = (Ir) => padding.left + ((Ir - IrMin) / (IrMax - IrMin)) * (width - padding.left - padding.right);
    const yScaleId = (Id) => (height - padding.bottom) - (((Id - IdMin) / (IdMax - IdMin)) * (height - padding.top - padding.bottom));
    const xUnscaleId = (x) => IrMin + ((x - padding.left) / (width - padding.left - padding.right)) * (IrMax - IrMin);
    const yUnscaleId = (y) => IdMin + (((height - padding.bottom - y) / (height - padding.top - padding.bottom)) * (IdMax - IdMin));
    const xTicksId = Array.from({ length: 6 }).map((_, i) => IrMin + i * (IrMax - IrMin) / 5);
    const yTicksId = Array.from({ length: 6 }).map((_, i) => IdMin + i * (IdMax - IdMin) / 5);
    const onMouseMoveId = (e) => {
      if (!showTooltips) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left; const y = e.clientY - rect.top;
      const Irq = xUnscaleId(x);
      const items = (series || []).map((s, i) => {
        const pts = (s.points || []).filter(p => isFinite(p.t));
        if (!pts.length) return null;
        let best = pts[0]; let dmin = Math.abs(best.I - Irq);
        for (let j = 1; j < pts.length; j++) {
          const d = Math.abs(pts[j].I - Irq);
          if (d < dmin) { dmin = d; best = pts[j]; }
        }
        const color = getColor(s, i);
        return { label: s.label || `特性${i + 1}`, color, I: best.I, t: best.t };
      }).filter(Boolean);
      setHoverInfo({ x, y, items });
    };
    const onMouseLeaveId = () => setHoverInfo(null);
    // 提取二次谐波闭锁系数（Kb）：取首个可用值用于徽标展示
    const kbList = (series || []).map(s => s?.meta?.secondHarmonicBlocking).filter(v => typeof v === 'number' && isFinite(v));
    const kb = kbList.length > 0 ? kbList[0] : null;
    return (
      <>
      <svg className="id-ir-overlay-svg" width={width} height={height} style={{ background: '#fff', border: '1px solid #eee' }} onMouseMove={onMouseMoveId} onMouseLeave={onMouseLeaveId}>
        {/* 坐标轴主线 */}
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        {/* 轴箭头 */}
        <line x1={width - padding.right - 10} y1={height - padding.bottom - 5} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        <line x1={width - padding.right - 10} y1={height - padding.bottom + 5} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        <line x1={padding.left - 5} y1={padding.top + 10} x2={padding.left} y2={padding.top} stroke="#999" strokeWidth={1} />
        <line x1={padding.left + 5} y1={padding.top + 10} x2={padding.left} y2={padding.top} stroke="#999" strokeWidth={1} />
        {/* 网格与刻度标签 */}
        {xTicksId.map((Ir, idx) => (
          <g key={`xt-id-${idx}`}>
            <line x1={xScaleId(Ir)} y1={padding.top} x2={xScaleId(Ir)} y2={height - padding.bottom} stroke="#f2f2f2" strokeWidth={1} />
            <text x={xScaleId(Ir) - 10} y={height - padding.bottom + 18} fontSize={12} fill="#666">{formatAxisValue(Ir, 'current')}</text>
          </g>
        ))}
        {yTicksId.map((Id, idx) => (
          <g key={`yt-id-${idx}`}>
            <line x1={padding.left} y1={yScaleId(Id)} x2={width - padding.right} y2={yScaleId(Id)} stroke="#f2f2f2" strokeWidth={1} />
            <text x={8} y={yScaleId(Id) + 4} fontSize={12} fill="#666">{formatAxisValue(Id, 'current')}</text>
          </g>
        ))}
        <text x={width - padding.right - 60} y={height - padding.bottom + 18} fontSize={12} fill="#333">制动电流 Ir</text>
        <text x={padding.left + 8} y={padding.top + 12} fontSize={12} fill="#333">差动电流 Id</text>
        {/* 坐标系徽标与 Kb 提示 */}
        <g aria-label="axes-badge-id-ir">
          <rect x={padding.left + 8} y={padding.top + 8} width={kb != null ? 220 : 120} height={kb != null ? 40 : 24} rx={6} ry={6} fill="#fafafa" stroke="#e6e6e6" />
          <text x={padding.left + 16} y={padding.top + 24} fontSize={12} fill="#444">坐标系: Id–Ir</text>
          {kb != null && (
            <text x={padding.left + 16} y={padding.top + 38} fontSize={12} fill="#666">Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</text>
          )}
        </g>
        {/* 原点标记 */}
        <g>
          <line x1={padding.left - 4} y1={height - padding.bottom} x2={padding.left + 4} y2={height - padding.bottom} stroke="#bbb" />
          <line x1={padding.left} y1={height - padding.bottom - 4} x2={padding.left} y2={height - padding.bottom + 4} stroke="#bbb" />
        </g>

        {(series || []).map((s, i) => {
          const displayColor = getColor(s, i);
          const pts = (s.points || []).sort((a, b) => a.I - b.I);
          const d = pts.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${xScaleId(p.I)},${yScaleId(p.t)}`).join(' ');
          const characteristic = s.meta?.characteristic || {};
          const basePickup = Number(characteristic.diffPickup) || 0;
          const baseSlope = Number(characteristic.slope);
          const inst = Number(characteristic.instantaneousSecondary);
          // 分段：若提供 segments 则按 threshold 排序并连续绘制；否则提供默认两段 K=0 与 K=baseSlope
          const rawSegs = Array.isArray(characteristic.segments) ? characteristic.segments.map(seg => ({ slope: Number(seg.slope), threshold: Number(seg.threshold), label: seg.label })) : [];
          const segsSorted = rawSegs.filter(sg => isFinite(sg.threshold) && !Number.isNaN(sg.slope)).sort((a, b) => a.threshold - b.threshold);
          const segs = (() => {
            const arr = [];
            if (basePickup > 0 && (segsSorted.length === 0 || segsSorted[0].threshold > basePickup)) {
              arr.push({ slope: 0, threshold: basePickup, label: 'K=0' });
            }
            arr.push(...segsSorted);
            // 末段：达到 IrMax 后延伸，使用默认斜率
            const kLast = isFinite(baseSlope) ? baseSlope : (arr.length > 0 && isFinite(arr[arr.length - 1].slope) ? arr[arr.length - 1].slope : 0.5);
            arr.push({ slope: kLast, threshold: Infinity, label: `K=${kLast}` });
            return arr;
          })();

          // 绘制原始曲线（由点集）
          return (
            <g key={`serie-id-${i}`}>
              <path d={d} stroke={displayColor} strokeWidth={2} fill="none" />
              {pts.length > 0 && (
                <text x={xScaleId(pts[pts.length - 1].I) - 60} y={yScaleId(pts[pts.length - 1].t) - 6} fontSize={12} fill={displayColor}>
                  {s.label || `特性${i + 1}`}
                </text>
              )}
              {/* 阈值圆点：基础差动定值 */}
              {isFinite(basePickup) && basePickup > 0 && (
                <circle cx={xScaleId(0)} cy={yScaleId(basePickup)} r={3} fill={displayColor} stroke="#fff" strokeWidth={1} />
              )}
              {/* 分段比率制动线（连续拼接） */}
              {(() => {
                let Ir_s = 0;
                let Id_s = basePickup;
                const segElems = [];
                for (let si = 0; si < segs.length; si++) {
                  const k = Number(segs[si].slope) || 0;
                  const Ir_e_raw = segs[si].threshold;
                  const Ir_e = Math.min(isFinite(Ir_e_raw) ? Ir_e_raw : IrMax, IrMax);
                  if (Ir_e <= Ir_s) continue;
                  const x1 = xScaleId(Ir_s);
                  const y1 = yScaleId(Id_s);
                  const Id_e = Id_s + k * (Ir_e - Ir_s);
                  const x2 = xScaleId(Ir_e);
                  const y2 = yScaleId(Id_e);
                  segElems.push(<path key={`seg-${i}-${si}`} d={`M ${x1},${y1} L ${x2},${y2}`} stroke={displayColor} strokeWidth={1.5} strokeDasharray={k === 0 ? '4,3' : '3,2'} fill="none" opacity={0.9} />);
                  // 阈值圆点（段终点）
                  if (isFinite(Ir_e_raw) && Ir_e < IrMax - 1e-6) {
                    segElems.push(<circle key={`seg-dot-${i}-${si}`} cx={x2} cy={y2} r={3} fill={displayColor} stroke="#fff" strokeWidth={1} />);
                  }
                  // 阈值竖线与标签（非无限阈值）
                  if (isFinite(Ir_e_raw) && Ir_e < IrMax - 1e-6) {
                    segElems.push(<line key={`th-${i}-${si}`} x1={xScaleId(Ir_e)} y1={padding.top} x2={xScaleId(Ir_e)} y2={height - padding.bottom} stroke="#e9e9e9" strokeDasharray="4,3" />);
                    segElems.push(<text key={`thlab-${i}-${si}`} x={xScaleId(Ir_e) - 18} y={height - padding.bottom + 18} fontSize={12} fill="#888">Ir≈{formatAxisValue(Ir_e, 'current')}</text>);
                  }
                  // 段内斜率标注（居中）
                  const Ir_mid = Ir_s + (Ir_e - Ir_s) / 2;
                  const Id_mid = Id_s + k * (Ir_mid - Ir_s);
                  segElems.push(<text key={`klab-${i}-${si}`} x={xScaleId(Ir_mid) + 6} y={yScaleId(Id_mid) - 6} fontSize={12} fill={displayColor}>{segs[si].label || `K=${isFinite(k) ? k.toFixed(2) : '?'}`}</text>);
                  // 下一段起点连续
                  Ir_s = Ir_e;
                  Id_s = Id_e;
                  if (Ir_s >= IrMax - 1e-6) break;
                }
                return <g>{segElems}</g>;
              })()}
              {/* 速断水平线 */}
              {isFinite(inst) && inst > 0 && (
                <g>
                  <line x1={padding.left} y1={yScaleId(inst)} x2={width - padding.right} y2={yScaleId(inst)} stroke="#fa541c" strokeDasharray="6,4" />
                  <text x={padding.left + 6} y={yScaleId(inst) - 6} fontSize={12} fill="#fa541c">Id速断≈{formatAxisValue(inst, 'current')}</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
      {/* 悬浮提示，仅 Id-Ir 坐标 */}
      {axesType === 'Id-Ir' && showTooltips && hoverInfo && (
        <div
          role="tooltip"
          aria-live="polite"
          style={{ position: 'absolute', left: hoverInfo.x + 12, top: hoverInfo.y + 12, background: '#ffffff', border: '1px solid #e6e6e6', borderRadius: 6, padding: '6px 8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontSize: 12 }}
        >
          {/* Kb 提示（若可用） */}
          {(() => {
            const kbList = (series || []).map(s => s?.meta?.secondHarmonicBlocking).filter(v => typeof v === 'number' && isFinite(v));
            const kb = kbList.length > 0 ? kbList[0] : null;
            return kb != null ? (
              <div style={{ color: '#666', marginBottom: 4 }}>Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</div>
            ) : null;
          })()}
          {hoverInfo.items.map((it, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: it.color }} />
              <span style={{ color: '#333' }}>{it.label}</span>
              <span style={{ color: '#666' }}>Ir≈{formatAxisValue(it.I, 'current')}</span>
              <span style={{ color: '#666' }}>Id≈{isFinite(it.t) ? formatAxisValue(it.t, 'current') : '—'}</span>
            </div>
          ))}
        </div>
      )}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{ overflowX: 'auto', position: 'relative' }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="TCC 叠加图"
      role="img"
    >
      {/* 问问 AI 按钮 */}
      <div style={{ position: 'absolute', right: 8, top: 8, zIndex: 10 }}>
        <Button size="small" icon={<RobotOutlined />} onClick={handleAskAI}>
          {t('ai.ask_button') || '问问 AI'}
        </Button>
      </div>
      {axesType === 'Z' ? renderZPlane() : axesType === 'Id-Ir' ? renderIdIrPlane() : (
      <>
      <svg
        className="it-overlay-svg"
        width={width}
        height={height}
        style={{ background: '#fff', border: '1px solid #eee', cursor: enableBoxZoom ? (dragRect ? 'crosshair' : 'zoom-in') : 'default' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={startDrag}
        onMouseUp={endDrag}
        onMouseMoveCapture={moveDrag}
      >
        {/* 坐标轴 */}
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
        {/* 刻度 */}
        {xTicks.map((I, idx) => {
          const x = xScale(I);
          return (
            <g key={`x-${idx}`}>
              <line x1={x} y1={height - padding.bottom} x2={x} y2={height - padding.bottom - 6} stroke="#aaa" />
              <text x={x - 12} y={height - padding.bottom + 18} fontSize={12} fill="#666">{formatAxisValue(I, 'current')}</text>
          </g>
        );
      })}
      {yTicks.map((T, idx) => {
        const y = yScale(T);
        return (
          <g key={`y-${idx}`}>
            <line x1={padding.left} y1={y} x2={padding.left + 6} y2={y} stroke="#aaa" />
            <text x={8} y={y + 4} fontSize={12} fill="#666">{formatAxisValue(T, 'time')}</text>
          </g>
        );
      })}

        {/* 多曲线路径 */}
        {(series || []).map((s, i) => {
          const displayColor = s.color || (colorBlindMode ? cbPalette[i % cbPalette.length] : palette[i % palette.length]);
          const points = (s.points || []).filter(p => p.t !== Infinity).sort((a, b) => a.I - b.I);
          const d = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${xScale(p.I)},${yScale(p.t)}`).join(' ');
          return (
            <g key={`serie-${i}`}>
              <path d={d} stroke={displayColor} strokeWidth={2} fill="none" />
              {/* 标签 */}
              {points.length > 0 && (
                <text x={xScale(points[points.length - 1].I) - 60} y={yScale(points[points.length - 1].t) - 6} fontSize={12} fill={displayColor}>
                  {s.label || `曲线${i + 1}`}
                </text>
              )}
              {/* 参考线：启动值、瞬时阈值、负荷基准（二次） */}
              {refLineKeys.includes(s.key || s.label) && (
                <>
                  {isFinite(s.pickup) && (
                    <line x1={xScale(s.pickup)} y1={padding.top} x2={xScale(s.pickup)} y2={height - padding.bottom} stroke={displayColor} strokeWidth={1} strokeDasharray="4,3" />
                  )}
                  {isFinite(s?.meta?.instantaneousPickup) && (
                    <line x1={xScale(s.meta.instantaneousPickup)} y1={padding.top} x2={xScale(s.meta.instantaneousPickup)} y2={height - padding.bottom} stroke={displayColor} strokeWidth={1} strokeDasharray="2,2" />
                  )}
                  {isFinite(s?.meta?.baseLoadSecondary) && (
                    <line x1={xScale(s.meta.baseLoadSecondary)} y1={padding.top} x2={xScale(s.meta.baseLoadSecondary)} y2={height - padding.bottom} stroke={displayColor} strokeWidth={1} strokeDasharray="6,3" />
                  )}
                </>
              )}
            </g>
          );
        })}
        {/* 框选区域 */}
        {dragRect && (
          <rect
            x={Math.min(dragRect.x1, dragRect.x2)}
            y={Math.min(dragRect.y1, dragRect.y2)}
            width={Math.abs(dragRect.x2 - dragRect.x1)}
            height={Math.abs(dragRect.y2 - dragRect.y1)}
            fill="#1677ff22"
            stroke="#1677ff"
            strokeDasharray="5,5"
          />
        )}
      </svg>

      {/* 悬浮提示，仅 I-t 坐标 */}
      {axesType === 'I-t' && showTooltips && hoverInfo && (
        <div
          role="tooltip"
          aria-live="polite"
          style={{ position: 'absolute', left: hoverInfo.x + 12, top: hoverInfo.y + 12, background: '#ffffff', border: '1px solid #e6e6e6', borderRadius: 6, padding: '6px 8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontSize: 12 }}
        >
          {/* Kb 提示（若可用） */}
          {(() => {
            const kbList = (series || []).map(s => s?.meta?.secondHarmonicBlocking).filter(v => typeof v === 'number' && isFinite(v));
            const kb = kbList.length > 0 ? kbList[0] : null;
            return kb != null ? (
              <div style={{ color: '#666', marginBottom: 4 }}>Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</div>
            ) : null;
          })()}
          {hoverInfo.items.map((it, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: it.color }} />
              <span style={{ color: '#333' }}>{it.label}</span>
              <span style={{ color: '#666' }}>I≈{formatAxisValue(it.I, 'current')}</span>
              <span style={{ color: '#666' }}>t≈{isFinite(it.t) ? formatAxisValue(it.t, 'time') : '∞'}</span>
            </div>
          ))}
        </div>
      )}

      {/* 图例 */}
      {showLegend && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {/* Kb 提示（若可用），作为图例的首项 */}
          {(() => {
            const kbList = (series || []).map(s => s?.meta?.secondHarmonicBlocking).filter(v => typeof v === 'number' && isFinite(v));
            const kb = kbList.length > 0 ? kbList[0] : null;
            return kb != null ? (
              <div key="legend-kb" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 16, height: 2, background: '#999' }} />
                <span style={{ color: '#666' }}>Kb≈{Number(kb).toFixed(2)}（二次谐波闭锁）</span>
              </div>
            ) : null;
          })()}
          {(series || []).map((s, i) => {
            const color = getColor(s, i);
            return (
              <div key={`legend-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 16, height: 2, background: color }} />
                <span style={{ color: '#555' }}>{s.label || `曲线${i + 1}`}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* AI 结果面板 */}
      {aiOpen && (
        <div style={{ position: 'absolute', right: 8, top: 40, width: Math.min(420, Math.max(280, width * 0.4)), maxHeight: height - 64, background: '#fff', border: '1px solid #e6e6e6', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.12)', padding: 10, zIndex: 9, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 600, color: '#333' }}>{t('ai.panel.title') || '曲线知识助手'}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(aiText)}>{t('ai.panel.copy') || '复制内容'}</Button>
              {aiPrompt ? (<Button size="small" icon={<EyeOutlined />} onClick={() => copyToClipboard(aiPrompt)}>{t('ai.panel.prompt_view') || '查看提示词'}</Button>) : null}
              <Button size="small" icon={<CloseOutlined />} onClick={() => setAiOpen(false)}>{t('ai.panel.close') || '关闭'}</Button>
            </div>
          </div>
          <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>
            {aiSource === 'external' ? (t('ai.panel.source.external') || '来自外部模型') : (t('ai.panel.source.local') || '来自本地分析')}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {aiLoading ? (
              <div style={{ color: '#666' }}>正在生成分析...</div>
            ) : aiError ? (
              <div style={{ color: '#d4380d' }}>{aiError}</div>
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6 }}>{aiText}</div>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default CurveOverlay;